import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { Plus } from 'lucide-react';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import CertificateSavePanel from '@/temu-platform/components/CertificateSavePanel';
import BatchQueryModal from '@/temu-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/temu-platform/modules/products/components/batchQueryUtils';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import TemuProductThumb from '@/temu-platform/modules/products/components/TemuProductThumb';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import SyncPlatformJsonModal from '@/temu-platform/components/SyncPlatformJsonModal';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { extractTemuList, pickField } from '@/temu-platform/utils/temuResponse';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { showToast } from '@/utils/toast';
import ComplianceAutoBindRuleModal from '@/temu-platform/modules/compliance/ComplianceAutoBindRuleModal';
import {
  readComplianceAutoBindErrors,
} from '@/temu-platform/modules/compliance/complianceAutoBindRules';
import {
  maybeRunCertAutoBindAfterSync,
} from '@/temu-platform/modules/compliance/complianceAutoBindRunner';
import { loadProductMetaBySkc } from '@/temu-platform/utils/temuProductMetaBySkc';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import { createPortal } from 'react-dom';
import '@/styles/temu-platform/temu-product-ops.css';

const FILTER_ALL = 'all';
const FILTER_MISSING = '0';
const FILTER_PENDING = '1';
const FILTER_APPROVED = '2';
const FILTER_REJECTED = '3';

/**
 * 状态维度：对齐卖家中心「资质上传状态」
 * 0 待上传 / 1 上传中 / 2 上传成功 / 3 上传失败（底层仍用 auditStatus）
 */
const FILTER_OPTIONS = [
  { id: FILTER_ALL, label: '全部' },
  { id: FILTER_MISSING, label: '待上传' },
  { id: FILTER_PENDING, label: '上传中' },
  { id: FILTER_APPROVED, label: '上传成功' },
  { id: FILTER_REJECTED, label: '上传失败' },
];

const UPLOAD_STATUS_LABELS = {
  0: '待上传',
  1: '上传中',
  2: '上传成功',
  3: '上传失败',
};

function resolveInitialFilter(value) {
  const raw = value == null ? '' : String(value).trim().toLowerCase();
  // 兼容旧链接
  if (raw === 'todo' || raw === 'missing' || raw === 'required') return FILTER_MISSING;
  if (raw === 'pending') return FILTER_PENDING;
  if (raw === 'approved') return FILTER_APPROVED;
  if (raw === 'rejected') return FILTER_REJECTED;
  const allowed = new Set(FILTER_OPTIONS.map((t) => t.id));
  if (value != null && allowed.has(String(value))) return String(value);
  return FILTER_ALL;
}

const ID_TYPE_OPTIONS = [
  { value: 'skc', label: 'SKC' },
  { value: 'spu', label: 'SPU' },
];

/**
 * 表头对齐卖家中心「商品资质」：SPU 维度 + 资质上传状态 / 合规要求与售卖影响。
 */
const TABLE_COLUMNS = [
  { key: 'check', label: '', platformField: null, width: '44px' },
  { key: 'product', label: '商品信息', platformField: 'product + spu', width: '22%' },
  { key: 'certName', label: '资质名称', platformField: 'certName', width: '10%' },
  { key: 'uploadStatus', label: '资质上传状态', platformField: 'auditStatus', width: '10%' },
  { key: 'certNameCn', label: '资质中文名', platformField: 'certNameCn', width: '14%' },
  { key: 'impact', label: '合规要求与售卖影响', platformField: 'complianceRequirements', width: '30%' },
  { key: 'ops', get label() { return fixedUi("操作"); }, platformField: null, width: '14%' },
];

const COL_SPAN = TABLE_COLUMNS.length;

const EMPTY_FILTERS = {
  idType: 'spu',
  idValue: '',
  certificateTypeId: '',
};

const EMPTY_QUERY = {
  idType: 'spu',
  keywords: '',
  certificateTypeId: '',
};

function emptyCertStatusCounts() {
  const counts = { all: 0 };
  FILTER_OPTIONS.filter((t) => t.id !== FILTER_ALL).forEach((t) => {
    counts[t.id] = 0;
  });
  return counts;
}

function parseTypeOptions(res) {
  const list = res?.certificateTypeInfoList
    || res?.data?.certificateTypeInfoList
    || res?.info?.certificateTypeInfoList
    || [];
  return Array.isArray(list) ? list.filter((item) => Number(item?.certificateTypeId) !== 844) : [];
}

/** 资质上传状态：优先 uploadStatusLabel，再按 auditStatus / reviewState */
function platformStatusLabel(row) {
  const fromApi = String(pickField(row, 'uploadStatusLabel') || '').trim();
  if (fromApi) return fromApi;
  const review = row?.uploadStatus ?? row?.reviewState ?? row?.auditStatus;
  if (review === 0 || review === '0') return UPLOAD_STATUS_LABELS[0];
  if (review === 1 || review === '1') return UPLOAD_STATUS_LABELS[1];
  if (review === 2 || review === '2') return UPLOAD_STATUS_LABELS[2];
  if (review === 3 || review === '3') return UPLOAD_STATUS_LABELS[3];
  const todo = pickField(row, 'todoStatus');
  if (todo === 'MISSING' || row?.missStatus === true) return UPLOAD_STATUS_LABELS[0];
  if (todo === 'PENDING_AUDIT') return UPLOAD_STATUS_LABELS[1];
  if (todo === 'APPROVED') return UPLOAD_STATUS_LABELS[2];
  if (todo === 'REJECTED') return UPLOAD_STATUS_LABELS[3];
  return '—';
}


const UPLOAD_TEXT_KEYS = { '待上传': 'certList.upload0', '上传中': 'certList.upload1', '上传成功': 'certList.upload2', '上传失败': 'certList.upload3' };
function reviewStatusText(row) {
  const raw = platformStatusLabel(row);
  const id = UPLOAD_TEXT_KEYS[raw];
  return id ? i18n.t(id, { ns: 'workbench' }) : raw;
}
function platformStatusModifier(row) {
  const review = Number(row?.uploadStatus ?? row?.reviewState ?? row?.auditStatus);
  if (review === 2) return 'success';
  if (review === 0 || review === 3) return 'failed';
  if (review === 1) return 'pending';
  const label = platformStatusLabel(row);
  if (label === UPLOAD_STATUS_LABELS[2]) return 'success';
  if (label === UPLOAD_STATUS_LABELS[0] || label === UPLOAD_STATUS_LABELS[3]) return 'failed';
  return 'pending';
}

function isUnreviewed(row) {
  const review = row?.uploadStatus ?? row?.reviewState ?? row?.auditStatus;
  if (review === 0 || review === '0') return true;
  if (review == null || review === '') {
    const todo = pickField(row, 'todoStatus');
    return todo === 'MISSING' || row?.missStatus === true || !todo;
  }
  return false;
}

function certNameOf(row) {
  return String(pickField(row, 'certName', 'certificateTypeCode') || '—').trim() || '—';
}

function certNameCnOf(row) {
  return String(
    pickField(row, 'certNameCn', 'certificateTypeName', 'certificateTypeValue', 'certificateName')
    || '—',
  ).trim() || '—';
}

function complianceRequirementsOf(row) {
  const list = Array.isArray(row?.complianceRequirements) ? row.complianceRequirements : [];
  if (list.length) {
    return list.map((item) => ({
      region: String(item?.region || item?.country || '—').trim() || '—',
      requirement: String(item?.requirement || item?.requirementDesc || item?.standard || '—').trim() || '—',
      requirementStatus: String(item?.requirementStatus || item?.statusLabel || '—').trim() || '—',
      saleImpact: String(item?.saleImpact || item?.sellImpact || '无影响').trim() || '无影响',
    }));
  }
  return [{
    region: '—',
    requirement: String(pickField(row, 'updateReason', 'rejectReason') || '—'),
    requirementStatus: platformStatusLabel(row),
    saleImpact: isUnreviewed(row) || Number(row?.reviewState) === 3 ? '缺失资质可能影响售卖' : '无影响',
  }];
}

function productMetaOf(row, metaBySkc = {}) {
  const product = row?.product && typeof row.product === 'object' ? row.product : null;
  const spu = String(
    pickField(row, 'spu', 'spuName', 'productId')
    || product?.spuName
    || product?.skcName
    || '',
  ).trim();
  const skc = String(pickField(row, 'skcName', 'skcNames', 'skc') || product?.skcName || spu || '').trim();
  const fromMap = (spu && metaBySkc[spu]) || (skc && metaBySkc[skc]) || {};
  return {
    skc,
    spu: spu || fromMap.spuName || skc || '',
    title: pickField(row, 'productTitle', 'title') || product?.title || fromMap.title || '',
    imageUrl: pickField(row, 'productImageUrl', 'mainImageUrl', 'imageUrl')
      || product?.imageUrl
      || product?.image
      || fromMap.imageUrl
      || '',
    supplierCode: fromMap.supplierCode || pickField(row, 'supplierCode', 'supplier_code') || product?.supplierCode || '',
  };
}

function rowKey(row, index) {
  return pickField(row, 'certificateId', 'id', 'poolSn', 'certificateNo', 'certificatePoolId') || `cert-${index}`;
}

function selectionId(row, index) {
  return pickField(row, 'certificateId') || rowKey(row, index);
}

/**
 * 产品证书资质链路：
 * ① 拉取 cert.query → 各 SPU 待上传/审核中的证书类型
 * ② 编辑：productId + certType → queryNeedUploadItems 查填写规则
 * ③ 提交 uploadProductCert
 */
export default function CertificateListPage({
  initialFilter = FILTER_ALL,
  embedded: _embedded = false,
  syncKinds = ['certificate', 'certAutoBind'],
  active = true,
} = {}) {
  const { t } = useTranslation('workbench');
  const { shopDimensionTabs, concreteScopeId } = useTemuPageShopScope();
  const shopId = concreteScopeId;

  const [statusFilter, setStatusFilter] = useState(() => resolveInitialFilter(initialFilter));
  const [filterDraft, setFilterDraft] = useState(() => ({ ...EMPTY_FILTERS }));
  const [query, setQuery] = useState(() => ({ ...EMPTY_QUERY }));
  const [queryNonce, setQueryNonce] = useState(0);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState(() => emptyCertStatusCounts());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [seed, setSeed] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [platformJsonOpen, setPlatformJsonOpen] = useState(false);
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [productMetaBySkc, setProductMetaBySkc] = useState({});
  const [autoBindErrors, setAutoBindErrors] = useState({});
  const [autoBindErrorSkc, setAutoBindErrorSkc] = useState('');
  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();

  const load = useCallback(async () => {
    if (!active) return;
    if (!shopId) {
      setRows([]);
      setTotal(0);
      setStatusCounts(emptyCertStatusCounts());
      setProductMetaBySkc({});
      return;
    }
    setLoading(true);
    try {
      const status = resolveInitialFilter(statusFilter);
      const res = await temuPlatformAPI.listCertificates(shopId, {
        pageNum: page,
        pageSize,
        source: 'cached',
        status: status !== FILTER_ALL ? status : undefined,
        idType: query.idType || 'spu',
        keywords: query.keywords || undefined,
        keyword: query.keywords || undefined,
        certificateTypeId: query.certificateTypeId || undefined,
      });
      const list = extractTemuList(res);
      setRows(list);
      setTotal(Number(res?.total) || 0);
      const rawCounts = res?.statusCounts && typeof res.statusCounts === 'object' ? res.statusCounts : {};
      const nextCounts = emptyCertStatusCounts();
      Object.keys(nextCounts).forEach((key) => {
        if (rawCounts[key] != null) nextCounts[key] = Number(rawCounts[key]) || 0;
      });
      if (rawCounts.all != null) nextCounts.all = Number(rawCounts.all) || 0;
      else {
        nextCounts.all = FILTER_OPTIONS
          .filter((t) => t.id !== FILTER_ALL)
          .reduce((sum, t) => sum + (Number(nextCounts[t.id]) || 0), 0);
      }
      setStatusCounts(nextCounts);
      const ids = list
        .map((row) => String(pickField(row, 'spu', 'spuName', 'productId', 'skcName', 'skcNames') || '').trim())
        .filter(Boolean);
      if (ids.length) {
        const meta = await loadProductMetaBySkc(shopId, ids, { enrichCategoryPath: false });
        setProductMetaBySkc(meta);
      } else {
        setProductMetaBySkc({});
      }
    } catch (e) {
      setRows([]);
      setTotal(0);
      setStatusCounts(emptyCertStatusCounts());
      setProductMetaBySkc({});
      showToast(e?.message || t('certList.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, page, pageSize, statusFilter, query.idType, query.keywords, query.certificateTypeId, queryNonce, active, t]);

  useEffect(() => {
    if (!active) return;
    load();
  }, [load, active]);

  useEffect(() => {
    setStatusFilter(resolveInitialFilter(initialFilter));
    setFilterDraft({ ...EMPTY_FILTERS });
    setQuery({ ...EMPTY_QUERY });
    setBatchModalOpen(false);
    setPage(1);
    setQueryNonce((n) => n + 1);
    setLastSyncedAt('');
    setPersistedSyncedAt('');
    setSelectedKeys(new Set());
    setTypeOptions([]);
    setStatusCounts(emptyCertStatusCounts());
  }, [shopId, initialFilter]);

  useEffect(() => {
    if (!shopId || !active) return undefined;
    let cancelled = false;
    temuPlatformAPI.listCertificateTypes(shopId)
      .then((res) => {
        if (!cancelled) setTypeOptions(parseTypeOptions(res));
      })
      .catch(() => {
        if (!cancelled) setTypeOptions([]);
      });
    return () => { cancelled = true; };
  }, [shopId, active]);

  useEffect(() => {
    if (!shopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopSyncStatus(shopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const cert = modules.find((m) => String(m.module || '').toUpperCase() === 'CERTIFICATE');
        const at = cert?.lastSyncEndAt || cert?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [shopId]);

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onQuery = () => {
    const tokens = parseBatchTokens(filterDraft.idValue).slice(0, BATCH_QUERY_LIMIT);
    if (shopId) setLoading(true);
    setPage(1);
    setQueryNonce((n) => {
      const next = n + 1;
      setQuery({
        idType: filterDraft.idType || 'spu',
        keywords: tokens.join(','),
        certificateTypeId: filterDraft.certificateTypeId || '',
      });
      return next;
    });
  };

  const onReset = () => {
    setFilterDraft({ ...EMPTY_FILTERS });
    setQuery({ ...EMPTY_QUERY });
    setStatusFilter(FILTER_ALL);
    setPage(1);
    setSelectedKeys(new Set());
    setQueryNonce((n) => n + 1);
  };

  const syncSpuName = (() => {
    if (query.idType !== 'spu' || !query.keywords) return '';
    const tokens = parseBatchTokens(query.keywords);
    return tokens.length === 1 ? tokens[0] : '';
  })();

  const syncCertificatesAction = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'TEMU' }), 'warning');
      return;
    }
    const payload = syncSpuName ? { spuName: syncSpuName } : {};
    const track = await startJob({
      kind: 'certificate',
      shopId,
      payload,
      label: t('certList.syncQual'),
      silent: true,
    });
    if (!track?.id) return;
    await new Promise((resolve) => {
      const unsub = onTrackDone(track.id, async (result) => {
        unsub();
        if (result?.ok) {
          const view = result.view || {};
          const count = Number(view.imported) || Number(view.certs) || Number(view.todoCount) || 0;
          setLastSyncedAt(String(view.finishedAt || new Date().toISOString()));
          let toastMsg = count > 0
            ? t('certList.syncQualDone', { count })
            : (view.message || t('certList.syncEmpty'));
          try {
            const auto = await maybeRunCertAutoBindAfterSync(shopId);
            if (auto?.ok > 0) {
              toastMsg = `${toastMsg}；${t('certList.autoBound', { count: auto.ok })}`;
            }
            if (auto?.fail > 0 || Object.keys(auto?.errorsBySkc || {}).length) {
              const prefRes = await temuPlatformAPI.getPreferences().catch(() => null);
              const prefs = prefRes?.preferences || prefRes || {};
              setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'certificate'));
            }
          } catch {
            /* 自动绑定失败不影响同步提示 */
          }
          showToast(toastMsg, count > 0 ? 'success' : 'warning');
          setPage(1);
          await load();
        } else {
          const err = result?.view?.error || result?.view?.message || result?.error || t('certList.syncFailed');
          showToast(err, 'error');
        }
        resolve();
      });
    });
  }, [shopId, syncSpuName, load, startJob, onTrackDone, t]);

  const {
    refresh: onSyncCertificates,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(syncCertificatesAction);

  const [seedingDemo, setSeedingDemo] = useState(false);
  const [autoBindModalOpen, setAutoBindModalOpen] = useState(false);

  useEffect(() => {
    if (!shopId || !active) {
      setAutoBindErrors({});
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getPreferences()
      .then((res) => {
        if (cancelled) return;
        const prefs = res?.preferences || res || {};
        setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'certificate'));
      })
      .catch(() => {
        if (!cancelled) setAutoBindErrors({});
      });
    return () => { cancelled = true; };
  }, [shopId, active]);

  const handleSeedDemo = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'TEMU' }), 'warning');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('certList.demoTitle'),
      message: t('certList.demoMsg'),
      tone: 'default',
      confirmText: t('certList.writeDemo'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setSeedingDemo(true);
    try {
      const res = await temuPlatformAPI.seedCertificateDemo(shopId);
      const count = Array.isArray(res?.certificateIds)
        ? res.certificateIds.length
        : (Number(res?.cacheUpserted) || 0);
      const coverage = Array.isArray(res?.statusCoverage) ? res.statusCoverage.length : 0;
      showToast(
        count > 0
          ? t('certList.demoWrittenQual', { count, coverage: coverage ? t('certList.coverageWrap', { count: coverage }) : '' })
          : t('certList.demoSeeded'),
        'success',
      );
      setPage(1);
      setStatusFilter(FILTER_ALL);
      setFilterDraft({ ...EMPTY_FILTERS });
      setQuery({ ...EMPTY_QUERY });
      setSelectedKeys(new Set());
      setQueryNonce((n) => n + 1);
      await load();
    } catch (e) {
      showToast(e?.message || t('certList.demoFailed'), 'error');
    } finally {
      setSeedingDemo(false);
    }
  }, [shopId, load, t]);

  const syncing = syncingLocal || isBusy('certificate', shopId);

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const idTypeLabel = ID_TYPE_OPTIONS.find((o) => o.value === filterDraft.idType)?.label || 'SPU';

  const statusTabs = (
    <div className="tpo-status-tabs" role="tablist" aria-label={t('certList.uploadStatusAria')}>
      {FILTER_OPTIONS.map((tab) => {
        const tabActive = statusFilter === tab.id;
        const count = Number(statusCounts[tab.id]) || 0;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tabActive}
            className={`tpo-status-tab${tabActive ? ' tpo-status-tab--active' : ''}`}
            onClick={() => {
              setStatusFilter(tab.id);
              setPage(1);
              setSelectedKeys(new Set());
            }}
          >
            {fixedUi(tab.label)}
            {count > 0 ? (
              <span className="tpo-status-tab__count">{count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );

  const selectableKeys = useMemo(
    () => rows.map((row, index) => selectionId(row, index)).filter(Boolean),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    if (!key) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openUpload = (row = null) => {
    if (row) {
      setSeed({
        productId: pickField(row, 'productId', 'spu', 'spuName') || pickField(row, 'skcName'),
        spuName: pickField(row, 'spuName'),
        skcName: pickField(row, 'skcName'),
        skcNames: pickField(row, 'skcNames'),
        certificateTypeId: pickField(row, 'certificateTypeId', 'certType'),
        certificateTypeCode: pickField(row, 'certificateTypeCode'),
        certificateDimension: pickField(row, 'certificateDimension') || 1,
        certificatePoolId: pickField(row, 'certificatePoolId'),
        poolSn: pickField(row, 'poolSn'),
        _ts: Date.now(),
      });
    } else {
      setSeed(null);
    }
    setPanelOpen(true);
  };

  const openBatchBind = () => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row, index) => {
      const key = selectionId(row, index);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
    const selected = Array.from(selectedKeys)
      .map((key) => map.get(key))
      .filter(Boolean);
    if (!selected.length) {
      showToast(t('certList.pickBind'), 'warning');
      return;
    }
    const skcs = [...new Set(
      selected
        .map((row) => pickField(row, 'skcName', 'skc', 'skcCode'))
        .filter(Boolean)
        .map((s) => String(s).trim()),
    )];
    if (!skcs.length) {
      showToast(t('certList.missingSkc'), 'error');
      return;
    }
    const first = selected[0];
    setSeed({
      skcName: skcs.join(','),
      skcNames: skcs.join(','),
      certificateTypeId: pickField(first, 'certificateTypeId'),
      certificateTypeCode: pickField(first, 'certificateTypeCode'),
      certificateDimension: 1,
      _ts: Date.now(),
    });
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setSeed(null);
  };

  const resolveSelectedRows = () => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row, index) => {
      const key = selectionId(row, index);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  };

  const writeCertificatesCsv = useCallback((exportRows, filename, metaBySkc = productMetaBySkc) => {
    downloadCsv(
      filename,
      ['spu', 'title', 'certName', 'uploadStatus', 'certNameCn', 'complianceRequirements', 'saleImpact'],
      exportRows.map((row) => {
        const product = productMetaOf(row, metaBySkc);
        const reqs = complianceRequirementsOf(row);
        return {
          spu: product.spu,
          title: product.title,
          certName: certNameOf(row),
          uploadStatus: platformStatusLabel(row),
          certNameCn: certNameCnOf(row),
          complianceRequirements: reqs.map((r) => `${r.region}: ${r.requirement}(${r.requirementStatus})`).join('；'),
          saleImpact: reqs.map((r) => r.saleImpact).join('；'),
        };
      }),
    );
  }, [productMetaBySkc]);

  const fetchAllCertificates = useCallback(async () => {
    if (!shopId) return [];
    const status = resolveInitialFilter(statusFilter);
    return fetchAllPages(async (pageNo, size) => {
      const res = await temuPlatformAPI.listCertificates(shopId, {
        pageNum: pageNo,
        pageSize: size,
        source: 'cached',
        status: status !== FILTER_ALL ? status : undefined,
        idType: query.idType || 'spu',
        keywords: query.keywords || undefined,
        keyword: query.keywords || undefined,
        certificateTypeId: query.certificateTypeId || undefined,
      });
      return {
        items: extractTemuList(res) || [],
        total: Number(res?.total) || 0,
      };
    });
  }, [shopId, statusFilter, query.idType, query.keywords, query.certificateTypeId]);

  const exportFiltered = useCallback(async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const exportRows = await fetchAllCertificates();
      if (!exportRows.length) {
        showToast(t('certLib.noneExport'), 'warning');
        return;
      }
      const skcs = exportRows
        .map((row) => String(pickField(row, 'skcName', 'skcNames') || '').trim())
        .filter(Boolean);
      const meta = skcs.length
        ? await loadProductMetaBySkc(shopId, skcs, { enrichCategoryPath: false })
        : {};
      writeCertificatesCsv(exportRows, `temu-certificates-filtered-${Date.now()}.csv`, meta);
      showToast(t('certLib.exported', { count: exportRows.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  }, [shopId, exporting, fetchAllCertificates, writeCertificatesCsv, t]);

  const exportPage = useCallback(() => {
    if (!rows.length) {
      showToast(t('certLib.noneExport'), 'warning');
      return;
    }
    writeCertificatesCsv(rows, `temu-certificates-page-${Date.now()}.csv`);
    showToast(t('certLib.exported', { count: rows.length }), 'success');
  }, [rows, writeCertificatesCsv, t]);

  const exportSelected = useCallback(() => {
    const exportRows = resolveSelectedRows();
    if (!exportRows.length) {
      showToast(t('certLib.pickExport'), 'warning');
      return;
    }
    writeCertificatesCsv(exportRows, `temu-certificates-selected-${Date.now()}.csv`);
    showToast(t('certLib.exportedSelected', { count: exportRows.length }), 'success');
  }, [selectedKeys, writeCertificatesCsv, t]);

  const onBatchDelete = async () => {
    if (!shopId || selectedKeys.size === 0) {
      showToast(t('certList.pickDeleteCache'), 'warning');
      return;
    }
    const ids = Array.from(selectedKeys).filter(Boolean);
    if (ids.length === 0) {
      showToast(t('certList.missingCertId'), 'error');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('returnList.batchDelete'),
      message: t('certList.batchDeleteMsg', { count: ids.length, platform: 'TEMU' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await temuPlatformAPI.batchDeleteCertificates(shopId, ids);
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('certList.deletedCache', { count }), 'success');
      } else {
        showToast(t('certList.noneDeletedCache'), 'warning');
      }
      setSelectedKeys(new Set());
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <TemuSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || deleting || seedingDemo || exporting}
            syncKinds={syncKinds}
            quotaKind="certificate"
            onSync={() => {
              if (!shopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'TEMU' }), 'warning');
                return;
              }
              onSyncCertificates();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('certList.syncHintTemu')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId || deleting}
            trailingExtra={(
              <button
                type="button"
                className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                disabled={!shopId}
                title={shopId
                  ? t('certList.jsonTitle')
                  : t('certList.jsonNeedShop')}
                onClick={() => setPlatformJsonOpen(true)}
              >
                {t('certList.platformJson')}
              </button>
            )}
            extraActions={[
              {
                key: 'auto-rules',
                label: t('certList.autoRule'),
                disabled: !shopId || deleting || seedingDemo || syncing || loading,
                onClick: () => setAutoBindModalOpen(true),
              },
              {
                key: 'batch-bind',
                label: selectedKeys.size > 0 ? t('certList.batchBindCount', { count: selectedKeys.size }) : t('certList.batchBind'),
                tone: 'primary',
                disabled: !shopId || deleting || selectedKeys.size === 0,
                onClick: openBatchBind,
              },
              {
                key: 'seed-demo',
                label: seedingDemo ? t('certList.seeding') : t('certList.loadDemo'),
                disabled: !shopId || seedingDemo || syncing || loading || deleting,
                onClick: handleSeedDemo,
              },
            ]}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--id-combo tpo-field--batch">
              <select
                className="tpo-select tpo-select--compact"
                value={filterDraft.idType}
                disabled={!shopId}
                onChange={(e) => updateField('idType', e.target.value)}
                aria-label={t('tiktokCatalog.idTypeAria')}
              >
                {ID_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.id === 'all' ? 'tiktokCatalog.all' : ('certList.upload' + opt.id), { defaultValue: opt.label })}</option>
                ))}
              </select>
              <input
                className="tpo-input"
                value={filterDraft.idValue}
                placeholder={t('tiktokCatalog.idPlaceholder', { label: idTypeLabel })}
                disabled={!shopId}
                onChange={(e) => updateField('idValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('tiktokCatalog.batchInput', { label: idTypeLabel })}
                title={t('tiktokCatalog.batchQuery')}
                disabled={!shopId}
                onClick={() => setBatchModalOpen(true)}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="tpo-field tpo-field--inline tpo-field--cert-type">
              <span className="tpo-field__label">{t('certLib.colType')}</span>
              <select
                className="tpo-select tpo-select--cert-type"
                value={filterDraft.certificateTypeId}
                disabled={!shopId}
                onChange={(e) => updateField('certificateTypeId', e.target.value)}
                aria-label={t('certLib.colType')}
              >
                <option value="">{t('certLib.allTypes')}</option>
                {typeOptions.map((item) => (
                  <option key={item.certificateTypeId} value={String(item.certificateTypeId)}>
                    {item.certificateType || item.certificateTypeName || item.certificateTypeId}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="tpo-filter-bar__actions">
            <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onReset} disabled={!shopId}>
              {t('returnList.reset')}
            </button>
            <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onQuery} disabled={!shopId}>
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <BatchQueryModal
        open={batchModalOpen}
        title={t('certList.batchQueryId', { label: idTypeLabel })}
        initialValue={filterDraft.idValue}
        placeholder={t('certList.batchLine', { label: idTypeLabel })}
        onConfirm={(tokens) => {
          updateField('idValue', joinBatchTokens(tokens));
        }}
        onClose={() => setBatchModalOpen(false)}
      />

      <CertificateSavePanel
        open={panelOpen}
        shopId={shopId}
        seed={seed}
        onSaved={() => {
          closePanel();
          load();
        }}
        onClose={closePanel}
      />

      <ProductListCard
        tabs={stackShopDimensionTabs(shopDimensionTabs, statusTabs)}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('certList.querying')}
      >
        <div className="tpo-drafts__table-wrap tpo-certs-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-certs-table">
            <colgroup>
              {TABLE_COLUMNS.map((col) => (
                <col
                  key={col.key}
                  className={`tpo-certs-col tpo-certs-col--${col.key}`}
                  style={col.width ? { width: col.width } : undefined}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-certs-th tpo-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('certLib.selectAll')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {TABLE_COLUMNS.filter((c) => c.key !== 'check').map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className={`tpo-certs-th tpo-certs-th--${col.key}`}
                    title={col.platformField || undefined}
                  >
                    {i18n.t({ product: 'certList.colGoods', certificateTypeCode: 'certList.colMissingCode', certificateTypeName: 'certList.colMissingName', status: 'certList.colAudit', certName: 'certList.colQualName', uploadStatus: 'certList.colUpload', certNameCn: 'certList.colQualCn', impact: 'certList.colImpact', ops: 'certLib.colOps' }[col.key], { ns: 'workbench', defaultValue: col.label })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={COL_SPAN} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">
                        {!shopId
                          ? t('returnList.pickShop')
                          : loading
                            ? t('returnList.loading')
                            : statusFilter === FILTER_MISSING
                              ? t('certList.emptyUpload')
                              : t('certList.emptyQual')}
                      </p>
                      {!shopId || loading ? null : (
                        <p className="tpo-certs-empty__desc">
                          {statusFilter === FILTER_MISSING
                            ? t('certList.emptyDemoPullTemu')
                            : t('certList.emptyDemoFilterTemu')}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const key = selectionId(row, index);
                  const product = productMetaOf(row, productMetaBySkc);
                  const reqs = complianceRequirementsOf(row);
                  const errorKey = product.spu || product.skc;
                  return (
                    <tr key={rowKey(row, index)}>
                      <td
                        className="tpo-certs-td tpo-certs-td--check"
                        onClick={() => toggleOne(key)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('returnList.selectRow', { title: product.spu || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="tpo-certs-td tpo-certs-td--product">
                        <div className="tpo-photo-product">
                          {product.imageUrl ? (
                            <TemuProductThumb
                              imageUrl={product.imageUrl}
                              className="tpo-photo-product__img"
                              placeholderClassName="tpo-photo-product__img tpo-photo-product__img--empty"
                              alt={product.title || product.spu || ''}
                            />
                          ) : (
                            <div className="tpo-photo-product__img tpo-photo-product__img--empty" />
                          )}
                          <div className="tpo-photo-product__meta">
                            <HoverCopyField
                              value={product.title || ''}
                              className="tpo-photo-product__title"
                              popoverCopy
                            />
                            <HoverCopyField
                              label="SPU"
                              value={product.spu}
                              className="tpo-photo-product__skc"
                              separator=": "
                            />
                            {product.supplierCode ? (
                              <HoverCopyField
                                label={t('tiktokCatalog.supplier')}
                                value={product.supplierCode}
                                className="tpo-photo-product__skc"
                                separator=": "
                              />
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="tpo-certs-td tpo-certs-td--wrap">
                        <span className="tpo-certs-type-code">{certNameOf(row)}</span>
                      </td>
                      <td className="tpo-certs-td">
                        <span className={`tpo-records__status tpo-records__status--${platformStatusModifier(row)}`}>
                          {reviewStatusText(row)}
                        </span>
                      </td>
                      <td className="tpo-certs-td tpo-certs-td--wrap">
                        {certNameCnOf(row)}
                      </td>
                      <td className="tpo-certs-td tpo-certs-td--impact">
                        <table className="tpo-cert-impact">
                          <thead>
                            <tr>
                              <th>{t('certList.country')}</th>
                              <th>{t('certList.requirement')}</th>
                              <th>{t('certList.saleImpact')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reqs.map((req, reqIdx) => (
                              <tr key={`${key}-req-${reqIdx}`}>
                                <td>{req.region}</td>
                                <td>
                                  <div className="tpo-cert-impact__req">
                                    <span className={`tpo-records__status tpo-records__status--${
                                      req.requirementStatus.includes('成功') || req.requirementStatus.includes('已上传')
                                        ? 'success'
                                        : req.requirementStatus.includes('失败') || req.requirementStatus.includes('待上传')
                                          ? 'failed'
                                          : 'pending'
                                    }`}
                                    >
                                      {req.requirementStatus}
                                    </span>
                                    <span className="tpo-cert-impact__std">{req.requirement}</span>
                                  </div>
                                </td>
                                <td className={req.saleImpact !== '无影响' ? 'tpo-cert-impact__warn' : undefined}>
                                  {req.saleImpact === '无影响' ? t('certList.noImpact') : req.saleImpact === '缺失资质可能影响售卖' ? t('certList.impactSale') : req.saleImpact}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                      <td className="tpo-certs-td tpo-certs-td--ops">
                        <div className="tpo-drafts__actions tpo-cert-ops">
                          <button
                            type="button"
                            className="tpo-link-btn"
                            onClick={() => openUpload(row)}
                          >
                            {t('certList.detailEdit')}
                          </button>
                          <button
                            type="button"
                            className="tpo-link-btn"
                            onClick={() => openUpload(row)}
                          >
                            {isUnreviewed(row) ? t('certList.uploadQual') : t('certList.modify')}
                          </button>
                          {errorKey && autoBindErrors[errorKey] ? (
                            <button
                              type="button"
                              className="tpo-link-btn"
                              onClick={() => setAutoBindErrorSkc(errorKey)}
                            >
                              {t('certList.errorLog')}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('certList.qualPager')}
        />
      </ProductListCard>

      <ComplianceAutoBindRuleModal
        open={autoBindModalOpen}
        shopId={shopId}
        kind="certificate"
        busy={syncing || loading || deleting || seedingDemo}
        onClose={() => setAutoBindModalOpen(false)}
        onRan={async () => {
          const prefRes = await temuPlatformAPI.getPreferences().catch(() => null);
          const prefs = prefRes?.preferences || prefRes || {};
          setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'certificate'));
          load().catch(() => {});
        }}
      />
      <CertAutoBindErrorModal
        open={Boolean(autoBindErrorSkc)}
        skc={autoBindErrorSkc}
        reason={autoBindErrors[autoBindErrorSkc] || ''}
        onClose={() => setAutoBindErrorSkc('')}
      />
      <SyncPlatformJsonModal
        open={platformJsonOpen}
        shopId={shopId}
        dumpKind="certificate"
        title={t('certList.jsonQualTitle')}
        hint={t('certList.jsonQualHint')}
        onClose={() => setPlatformJsonOpen(false)}
      />
    </>
  );
}

function CertAutoBindErrorModal({ open, skc, reason, onClose }) {
  const { t } = useTranslation('workbench');
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button type="button" className="tpo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-cert-auto-bind-error-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-cert-auto-bind-error-title">{t('certList.errorTitle', { skc: skc || '—' })}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          <p className="tpo-label-fail__reason">{reason || t('certList.noError')}</p>
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onClose}>
            {t('certList.gotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
